import { action, computed, observable } from 'mobx';
import { observer } from 'mobx-react';
import {
    autoinject,
    bind,
    FormStore,
    inject,
    Input,
    Loader,
    Modal,
    ModalHandle,
    TasksStore,
    Title,
} from 'Mri.Web.Common';
import * as React from 'react';
import { RouteComponentProps } from 'react-router';
import { Form, FormGroup } from 'reactstrap';
import { AppStore } from '../../Core/AppStore';
import { CsvStore, IImportModel } from '../CsvStore';
import { ImportFormList } from './ImportFormList';

export interface IImportsViewProps extends RouteComponentProps<undefined> {}

@autoinject
@observer
export class ImportsView extends React.Component<IImportsViewProps> {
    public render() {
        return (
            <div className="container-fluid pt-3">
                <Title>Imports</Title>

                <Form encType="multipart/form-data" onSubmit={this.uploadAsync} className="form-inline mb-3">
                    <FormGroup>
                        <input
                            type="submit"
                            value="Upload"
                            className="btn btn-primary me-3"
                            disabled={!this.importForm.fields.files.value?.length}
                        />
                    </FormGroup>

                    <FormGroup className="d-inline-block">
                        <Input
                            className="d-inline-block"
                            type="file"
                            id="display-csv-import"
                            field={this.importForm.fields.files}
                        />
                    </FormGroup>
                </Form>

                <Loader message="Creating Upload..." showSpinner loading={this.loading} />
                <Loader message="Submitting..." showSpinner loading={this.submitting} />

                {!!this.formStores.length && !this.submitting && !this.loading && (
                    <div className="card p-0">
                        <ImportFormList
                            formStores={this.formStores}
                            columns={this.columns}
                            onSubmit={this.saveAsync}
                            emptyColumns={this.emptyColumns}
                        />
                    </div>
                )}

                <Modal
                    className="modal-sm"
                    title="Confirm Upload"
                    modalHandle={this.modalHandle}
                    btnClass="w-50"
                    footerClass="d-flex justify-content-center"
                >
                    <div className="text-center">
                        Over 50% of the data under {this.emptyColumns.join(', ')} column(s) is/are empty, blank or 0.
                        Are you sure you still want to upload?
                    </div>
                </Modal>
            </div>
        );
    }

    @computed
    private get emptyColumns(): string[] {
        return this.columns
            .map(c => {
                const emptyCount = this.formStores.reduce((e, f) => {
                    switch (c) {
                        case 'Comments':
                            if (!f.fields.displayConnectionData.fields.comments.value) e++;
                            break;
                        case 'CameraIP':
                            if (!f.fields.displayConnectionData.fields.cameraIP.value) e++;
                            break;
                        case 'Active':
                            if (!f.fields.displayConnectionData.fields.active.value) e++;
                            break;
                        case 'Input1IP':
                            if (!f.fields.displayConnectionData.fields.input1IP.value) e++;
                            break;
                        case 'Input1Password':
                            if (!f.fields.displayConnectionData.fields.input1Password.value) e++;
                            break;
                        case 'Input2IP':
                            if (!f.fields.displayConnectionData.fields.input2IP.value) e++;
                            break;
                        case 'Input2Password':
                            if (!f.fields.displayConnectionData.fields.input2Password.value) e++;
                            break;
                        case 'DisplayBoardIP':
                            if (!f.fields.displayConnectionData.fields.displayBoardIP.value) e++;
                            break;
                        case 'DisplayBoardPort':
                            if (!f.fields.displayConnectionData.fields.displayBoardPort.value) e++;
                            break;
                        case 'DisplayBoardWebsitePort':
                            if (!f.fields.displayConnectionData.fields.displayBoardWebsitePort.value) e++;
                            break;
                        case 'NocServer':
                            if (!f.fields.displayConnectionData.fields.nocServer.value) e++;
                            break;
                        case 'Contract':
                            if (!f.fields.contract.value) e++;
                            break;
                        case 'CustomerSiteID':
                            if (!f.fields.unitLocation.fields.customerSiteID.value) e++;
                            break;
                        case 'CustomerSiteID2':
                            if (!f.fields.unitLocation.fields.customerSiteID2.value) e++;
                            break;
                        case 'CustomerDisplayID':
                            if (!f.fields.displayConnectionData.fields.customerDisplayID.value) e++;
                            break;
                        case 'CustomerDisplayID2':
                            if (!f.fields.displayConnectionData.fields.customerDisplayID2.value) e++;
                            break;
                        case 'SslPort':
                            if (!f.fields.displayConnectionData.fields.sslPort.value) e++;
                            break;
                        case 'ModemIP':
                            if (!f.fields.displayConnectionData.fields.modemIP.value) e++;
                            break;
                        case 'Address1':
                            if (!f.fields.unitLocation.fields.address1.value) e++;
                            break;
                        case 'Address2':
                            if (!f.fields.unitLocation.fields.address2.value) e++;
                            break;
                        case 'City':
                            if (!f.fields.unitLocation.fields.city.value) e++;
                            break;
                        case 'StateOrProvince':
                            if (!f.fields.unitLocation.fields.stateOrProvince.value) e++;
                            break;
                        case 'Country':
                            if (!f.fields.unitLocation.fields.country.value) e++;
                            break;
                        case 'PostalCode':
                            if (!f.fields.unitLocation.fields.postalCode.value) e++;
                            break;
                        case 'Description':
                            if (!f.fields.unitLocation.fields.description.value) e++;
                            break;
                    }
                    return e;
                }, 0);

                return {
                    name: c,
                    emptyCount,
                };
            })
            .filter(o => o.emptyCount / this.formStores.length > 0.5)
            .map(o => o.name);
    }

    @bind
    @action
    private async uploadAsync(event: React.FormEvent<HTMLFormElement>) {
        event.preventDefault();
        this.loading = true;

        const task = this.tasksStore.startSessionTask(`Upload Data`);

        try {
            const res = await this.csvStore.previewImportAsync(this.importForm.values);
            this.columns = res.columns;
            this.displays = res.models;
            this.formStores = this.displays.map(d => new FormStore(d));
            task.complete();
        } catch (e: any) {
            task.error(e);
        } finally {
            this.loading = false;
        }
    }

    @bind
    @action
    private async saveAsync(event: React.FormEvent<HTMLFormElement>) {
        event.preventDefault();
        const models = this.formStores.map(f => f.values);

        for (const formStore of this.formStores) {
            formStore.onSubmit();

            if (!formStore.validate()) {
                console.log('invalid');
                return;
            }
        }

        if (this.emptyColumns.length) {
            if (!(await this.modalHandle.confirm())) return;
        }

        this.submitting = true;

        try {
            await this.csvStore.saveAsync(models);

            this.formStores = [];
            this.displays = [];
        } catch (e: any) {
            if (e.modelState) {
                //this.dcdStore.clearGlobalErrors();
                //this.dcdStore.addErrors(e.modelState);
                this.appStore.alertHandle.set('danger', JSON.stringify(e.modelState));
            } else {
                console.error(e);
                this.appStore.alertHandle.setError(e, true);
            }
        } finally {
            this.submitting = false;
        }
    }

    @observable
    private readonly importForm = new FormStore<IImportModel>({
        files: null,
    });

    @observable
    private modalHandle = new ModalHandle();
    @observable
    private loading = false;
    @observable
    private columns: string[] = [];
    @observable
    private displays: Mri.Web.Internal.Controllers.Displays.UpdateDisplayModel[] = [];
    @observable
    private formStores: FormStore<Mri.Web.Internal.Controllers.Displays.UpdateDisplayModel>[] = [];
    @observable
    private submitting: boolean = false;
    @inject
    private appStore!: AppStore;
    @inject
    private csvStore!: CsvStore;
    @inject
    private tasksStore!: TasksStore;
}
